import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import { useCallback, useState } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { ReajusteCadastroForm } from "../../../schemas/FinanceiroReajuste_cadastro/FinanceiroReajuste_cadastro";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DropDown } from "../../../../../../common/domain/models/dropdown";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { FormPercentageNegativeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_percentage_masked_input_negativo";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";

/**
 * Reajuste Cadastro Modal 
 */

type Props = {
    open: boolean,
    saving: boolean,
    hookForm: UseFormReturn<ReajusteCadastroForm>,
    selectedIdReajuste?: number,
    ddlContratosFinanceiro: DropDown[],

    handleOpenChangeHistory: (idTabela: number | null, tipoTabela: LogTipoTabela | null) => void
    onSubmit: (form: ReajusteCadastroForm) => void,
    onClose: () => void,
};

export const CadastroReajusteModal = ({
    open,
    saving,
    hookForm,
    selectedIdReajuste,
    ddlContratosFinanceiro,
    handleOpenChangeHistory,
    onSubmit,
    onClose,

}: Props) => {
    const [controleContratosIgnorados, setControleContratosIgnorados] = useState<boolean>(false);

    const [ddlMeses] = useState<DropDown[]>([
        { id: 1, title: "Janeiro" },
        { id: 2, title: "Fevereiro" },
        { id: 3, title: "Março" },
        { id: 4, title: "Abril" },
        { id: 5, title: "Maio" },
        { id: 6, title: "Junho" },
        { id: 7, title: "Julho" },
        { id: 8, title: "Agosto" },
        { id: 9, title: "Setembro" },
        { id: 10, title: "Outubro" },
        { id: 11, title: "Novembro" },
        { id: 12, title: "Dezembro" },
    ]);

    const handleSubmit = useCallback((form: ReajusteCadastroForm) => {
        if (!saving) {
            onSubmit(form);
            setControleContratosIgnorados(false);
            hookForm.setValue("idsContratosFinanceiroIgnorados", []);
        }

    }, [saving, hookForm, onSubmit]);

    const handleClose = useCallback(() => {
        if (!saving) {
            onClose();
            setControleContratosIgnorados(false);
            hookForm.setValue("idsContratosFinanceiroIgnorados", []);
        }

    }, [saving, hookForm, onClose]);


    return (

        <Dialog
            open={open}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit),
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "45rem",
                    },
                },
            }}>

            <DialogTitle>
                Cadastrar Reajuste
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Informações"} arrangement="fullRow">
                        <div className="w-full bg-white p-4  rounded-2xl shadow-sm space-y-3">

                            <DefaultFormAutocomplete
                                name='mesIndice'
                                required={true}
                                label='Mês do Índice'
                                options={ddlMeses}
                                getOptionLabel={x => x.title}
                                getOptionKey={x => x.id}
                                reactHookForm={hookForm} />

                            <FormPercentageNegativeMaskedInput
                                name='percentual'
                                label='Percentual Reajuste'
                                required={true}
                                reactHookForm={hookForm} />

                            <DefaultFormAutocomplete
                                name='mesReajuste'
                                required={true}
                                label='Reajuste a partir do mês'
                                options={ddlMeses}
                                getOptionLabel={x => x.title}
                                getOptionKey={x => x.id}
                                reactHookForm={hookForm} />


                            <FormMaskedInput
                                name={'anoReajuste'}
                                required={true}
                                label={'Reajuste a partir do Ano'}
                                mask='0000'
                                maskPlaceholder="____"
                                reactHookForm={hookForm}
                            />

                            <div className="flex items-center justify-between">
                                <label className="font-medium text-gray-700 ">Aplicar reajuste na entrada dos contratos?</label>
                                <FormRadioGroupInput
                                    name="aplicarEntrada"
                                    label=""
                                    readOnly={saving}
                                    options={[
                                        { id: 1, label: "Sim" },
                                        { id: 0, label: "Não" },
                                    ]}
                                    reactHookForm={hookForm}
                                />

                            </div>

                            <div className="pt-3">
                                <label className="block text-sm font-medium mb-2 text-red-700 dark:text-red-300">
                                    <span className="block text-center text-white bg-red-500 p-2 rounded">
                                        Clique aqui caso queira que algum contrato não seja reajustado:
                                        <PrimaryButton
                                            onClick={() => setControleContratosIgnorados(!controleContratosIgnorados)}
                                            buttonColor="red">
                                            Não reajustar os contratos.
                                        </PrimaryButton>
                                    </span>
                                </label>
                                {
                                    controleContratosIgnorados &&
                                    <DefaultFormMultiAutocomplete
                                        name='idsContratosFinanceiroIgnorados'
                                        required={false}
                                        label='Contratos Financeiros que não serão reajustados'
                                        options={ddlContratosFinanceiro}
                                        getOptionLabel={x => x.title}
                                        getOptionKey={x => x.id}
                                        reactHookForm={hookForm} />
                                }
                            </div>


                        </div>
                    </FormContainer>
                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full flex items-end justify-between px-4 pb-4 gap-4">
                    {
                        selectedIdReajuste &&
                        <div className="flex flex-row gap-2">
                            <PrimaryButton
                                buttonColor="yellow"
                                onClick={() => handleOpenChangeHistory(selectedIdReajuste ?? null, LogTipoTabela.REAJUSTE)}>
                                <span className="text-xs">
                                    Historico de alterações
                                </span>
                            </PrimaryButton>
                        </div>
                    }
                    <div className="flex-grow" />
                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog >
    );
}